import React, {
    createContext,
    useState,
    useMemo,
    useContext
} from 'react';
import styled from 'styled-components';
import NavLink from 'TMComponents/NavLink';
import { Breadcrumbs as BreadcrumbsText } from 'TMComponents/Typography/styles';
import { BodyReg } from 'TMComponents/Typography/';
import FlexWrapper from 'TMComponents/FlexWrapper';
import HeadTags from 'TMComponents/HeadTags';
import { node } from 'prop-types';

const Wrapper = styled.div`
    background-color: ${(props) => props.theme.colorSystem.primary[3]};
    padding: 12px 0 6px;
    position: relative;
`;

const BreadcrumbsContent = styled(FlexWrapper)`
    flex-wrap: wrap;
    gap: 8px;
    max-width: 1120px;
    margin: 0 auto;
    padding: 0 16px;

    @media (min-width: 768px) {
        padding: 0 32px;
    }
`;
const Crumb = styled(NavLink)`
    display: inline-block;
    color: ${({ theme, isLast }) =>
        isLast
            ? theme.colorSystem.UIText[1]
            : theme.colorSystem.primary[4]};
    font-weight: ${({ isLast }) => (isLast ? '700' : '600')};

    &:hover {
        text-decoration: ${({ isLast }) =>
            isLast ? 'none' : 'underline'};
    }
`;

const Text = styled(BodyReg)`
    ${BreadcrumbsText}
    margin: 0;
    color: inherit;
    font-weight: inherit;
`;

const Chevron = styled(BodyReg)`
    margin: 0;
    color: ${({ theme }) => theme.colorSystem.UIText[1]};
    font-weight: 700;
`;

const getSchema = (crumbs) => {
    const schemaCrumbs = crumbs.map(({ title, link }, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: title,
        item: `https://thinkmoney.co.uk${link}`
    }));

    return `{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":${JSON.stringify(
        schemaCrumbs
    )}}`;
};

const BreadcrumbsContext = createContext();

const BreadcrumbsContextProvider = ({ children }) => {
    const [crumbs, setCrumbs] = useState({ show: false, crumbList: [] });
    const crumbsState = useMemo(() => ({ crumbs, setCrumbs }), [crumbs]);
    return (
        <BreadcrumbsContext.Provider value={crumbsState}>
            {children}
        </BreadcrumbsContext.Provider>
    );
};

const Breadcrumbs = () => {
    const { crumbs } = useContext(BreadcrumbsContext);

    if (!crumbs?.show) return null;

    const { crumbList } = crumbs;

    return (
        <>
            <HeadTags>
                <script type="application/ld+json">
                    {getSchema(crumbList)}
                </script>
            </HeadTags>
            <Wrapper>
                <BreadcrumbsContent align="center">
                    <Crumb href="/">
                        <Text>Home</Text>
                    </Crumb>
                    {crumbList.map(({ title, link }, index) => (
                        <React.Fragment key={title}>
                            <Chevron>{'>'}</Chevron>
                            <Crumb
                                href={link}
                                isLast={index === crumbList.length - 1}>
                                <Text>{title}</Text>
                            </Crumb>
                        </React.Fragment>
                    ))}
                </BreadcrumbsContent>
            </Wrapper>
        </>
    );
};

BreadcrumbsContextProvider.propTypes = {
    children: node.isRequired
};

export default Breadcrumbs;
export {
    Breadcrumbs,
    BreadcrumbsContextProvider,
    BreadcrumbsContext,
    getSchema
};
